import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './RewardEarnedPage.css';
import logo from '../../../shared/assets/images/logo.png';
import { businessService, tapSessionService, authService, customerService, emailCollectionService } from '../../../shared/services/firebaseService';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { isSignInWithEmailLink } from 'firebase/auth';
import { auth } from '../../../config/firebase';

const RewardEarnedPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, customerData, refreshCustomerData } = useAuth();

  // Function to darken a hex color
  const darkenColor = (hex, amount = 0.3) => {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Parse RGB values
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Darken each component
    const darkenedR = Math.max(0, Math.floor(r * (1 - amount)));
    const darkenedG = Math.max(0, Math.floor(g * (1 - amount)));
    const darkenedB = Math.max(0, Math.floor(b * (1 - amount)));
    
    // Convert back to hex
    return `#${darkenedR.toString(16).padStart(2, '0')}${darkenedG.toString(16).padStart(2, '0')}${darkenedB.toString(16).padStart(2, '0')}`;
  };

  // Function to determine if a color is light or dark
  const isLightColor = (hex) => {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Parse RGB values
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate luminance using the relative luminance formula
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return true if luminance is greater than 0.5 (light color)
    return luminance > 0.5;
  };
  const [businessData, setBusinessData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const [showAnimation, setShowAnimation] = useState(false);
  const [animatedPoints, setAnimatedPoints] = useState(0);
  const [animatedPointsToGo, setAnimatedPointsToGo] = useState(0);
  const [transform, setTransform] = useState('');
  const [shadow, setShadow] = useState('');
  const [isPressed, setIsPressed] = useState(false);
  const [showPopAnimation, setShowPopAnimation] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [cardBackView, setCardBackView] = useState('howItWorks'); // 'howItWorks' or 'signup'
  const cardRef = React.useRef(null);
  
  // Auth states
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [customerPoints, setCustomerPoints] = useState(0);
  const [sessionData, setSessionData] = useState(null);
  const signInProcessedRef = React.useRef(false);
  const tapClaimedRef = React.useRef(false);

  // Handle passwordless email link sign-in on page load
  useEffect(() => {
    const handleEmailLinkSignIn = async () => {
      const url = window.location.href;
      
      // Prevent duplicate processing of the same sign-in link
      if (signInProcessedRef.current) {
        console.log('[RewardEarned] Sign-in already processed, skipping');
        return;
      }
      
      if (isSignInWithEmailLink(auth, url)) {
        console.log('[RewardEarned] Detected email link sign-in');
        signInProcessedRef.current = true; // Mark as processed immediately
        setAuthLoading(true);
        
        try {
          // Try to get email from multiple sources (in priority order)
          let userEmail = 
            searchParams.get('email') ||                    // 1. From URL parameter (no prompt!)
            window.localStorage.getItem('emailForSignIn'); // 2. From localStorage
          
          // Only prompt as last resort (different device/browser)
          if (!userEmail) {
            userEmail = window.prompt('Please provide your email for confirmation');
          }
          
          console.log('[RewardEarned] Using email for sign-in:', userEmail);
          
          if (userEmail) {
            await authService.completePasswordlessSignIn(url, userEmail);
            console.log('[RewardEarned] Email link sign-in successful');
            
            // Get session and business from URL if still present
            const sessionId = searchParams.get('sid');
            if (sessionId) {
              // Fetch session to get business UID
              const session = await tapSessionService.getTapSession(sessionId);
              if (session && session.businessUid && auth.currentUser && !tapClaimedRef.current) {
                tapClaimedRef.current = true; // Mark as claimed to prevent duplicates
                // Create customer profile (only creates if doesn't exist)
                console.log('[RewardEarned] Creating customer profile for:', auth.currentUser.uid);
                await authService.createCustomerProfile(auth.currentUser.uid, session.businessUid, userEmail);
                console.log('[RewardEarned] Customer profile ensured, claiming tap');
                await handleClaimTap(sessionId);
              }
            }
            
            // Clean up URL - remove email and Firebase parameters but keep sid
            const cleanUrl = new URL(window.location.href);
            cleanUrl.searchParams.delete('email');
            cleanUrl.searchParams.delete('apiKey');
            cleanUrl.searchParams.delete('oobCode');
            cleanUrl.searchParams.delete('mode');
            cleanUrl.searchParams.delete('lang');
            window.history.replaceState({}, '', cleanUrl.toString());
            console.log('[RewardEarned] URL cleaned up:', cleanUrl.toString());
          }
        } catch (error) {
          console.error('[RewardEarned] Error with email link sign-in:', error);
          setAuthError(error.message);
          signInProcessedRef.current = false; // Reset on error to allow retry
        } finally {
          setAuthLoading(false);
        }
      }
    };

    handleEmailLinkSignIn();
  }, [searchParams]);

  useEffect(() => {
    const fetchBusinessData = async () => {
      try {
        console.log('[RewardEarned] Starting to fetch business data...');
        
        // Get session ID from URL parameters
        const sessionId = searchParams.get('sid');
        console.log('[RewardEarned] Session ID from URL:', sessionId);
        
        if (!sessionId) {
          console.error('[RewardEarned] ERROR: Session ID not found in URL');
          setError('Session ID missing');
          navigate('/tapfailed');
          return;
        }

        // Fetch the tap session
        console.log('[RewardEarned] Fetching tap session...');
        const session = await tapSessionService.getTapSession(sessionId);
        console.log('[RewardEarned] Tap session response:', session);
        
        if (!session) {
          console.error('[RewardEarned] ERROR: Tap session not found in Firestore');
          setError('Session not found');
          navigate('/tapfailed');
          return;
        }

        // Store session data for later use
        setSessionData(session);

        // Check if session has expired
        const now = new Date();
        const expiresAt = session.expiresAt?.toDate();
        console.log('[RewardEarned] Session expires at:', expiresAt, 'Current time:', now);
        
        if (expiresAt && expiresAt < now) {
          console.error('[RewardEarned] ERROR: Tap session has expired');
          setError('Session expired');
          navigate('/tapfailed');
          return;
        }

        // Get business data using businessUid from session
        console.log('[RewardEarned] Fetching business data for:', session.businessUid);
        const business = await businessService.getBusinessData(session.businessUid);
        console.log('[RewardEarned] Business data response:', business);
        
        if (!business) {
          console.error('[RewardEarned] ERROR: Business not found in Firestore');
          setError('Business not found');
          navigate('/tapfailed');
          return;
        }

        console.log('[RewardEarned] SUCCESS: All data loaded successfully');
        setBusinessData(business);
        
        // If user is authenticated, fetch their points AND auto-claim the tap
        if (user) {
          try {
            const customerAssociation = await customerService.getCustomerData(user.uid);
            if (customerAssociation?.businessAssociations?.[session.businessUid]) {
              setCustomerPoints(customerAssociation.businessAssociations[session.businessUid].pointsBalance || 0);
            } else {
              // Document exists but no association with this business yet
              setCustomerPoints(0);
            }
            
            // Auto-claim tap for logged-in users (prevent duplicate claims)
            if (!tapClaimedRef.current) {
              tapClaimedRef.current = true;
              console.log('[RewardEarned] User already logged in, auto-claiming tap');
              
              // Ensure customer profile and business association exist
              await authService.createCustomerProfile(user.uid, session.businessUid, user.email);
              
              // Claim the tap session by calling cloud function directly
              console.log('[RewardEarned] Claiming tap with session:', sessionId);
              const result = await tapSessionService.claimTap(sessionId);
              console.log('[RewardEarned] Tap claimed successfully:', result);
              
              // Update customer points with the new total
              if (result.totalPoints !== undefined) {
                setCustomerPoints(result.totalPoints);
              }
              
              // Refresh customer data from Firestore
              const updatedCustomer = await customerService.getCustomerData(user.uid);
              if (updatedCustomer?.businessAssociations?.[session.businessUid]) {
                const latestPoints = updatedCustomer.businessAssociations[session.businessUid].pointsBalance || 0;
                console.log('[RewardEarned] Updated points from Firestore:', latestPoints);
                setCustomerPoints(latestPoints);
              }
              
              console.log('[RewardEarned] Tap auto-claimed successfully');
            }
            
          } catch (err) {
            console.error('[RewardEarned] Error fetching customer points:', err);
            // Silently fail - document might not exist yet for new users
            setCustomerPoints(0);
            tapClaimedRef.current = false; // Reset on error to allow retry
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error('[RewardEarned] EXCEPTION caught:', error);
        console.error('[RewardEarned] Error message:', error.message);
        console.error('[RewardEarned] Error stack:', error.stack);
        console.error('[RewardEarned] Error code:', error.code);
        setError(`Failed to load: ${error.message || error}`);
        // Don't navigate immediately, let the user see the error
        setLoading(false);
      }
    };

    fetchBusinessData();
  }, [searchParams, navigate, user]);

  useEffect(() => {
    if (!loading && businessData) {
      // Use actual customer points if available, otherwise show 0
      const pointsForReward = businessData.settings?.pointsforreward || 1200;
      const pointsEarned = customerPoints || 0;
      
      // First, ensure we show 0% progress and max points to go
      setAnimatedProgress(0);
      setShowAnimation(false);
      setAnimatedPointsToGo(pointsForReward);
      
      // Wait for the component to render with 0%, then start animation
      setTimeout(() => {
        setShowAnimation(true);
        const targetProgress = (pointsEarned / pointsForReward) * 100;
        const targetPoints = pointsEarned;
        const targetPointsToGo = pointsForReward - pointsEarned;
        const duration = 2500; // 2.5 seconds for more dramatic effect
        const startTime = Date.now();

        // Ease-out cubic function: starts fast, slows down smoothly
        const easeOutCubic = (t) => {
          return 1 - Math.pow(1 - t, 3);
        };

        const animateProgress = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          
          // Apply easing function
          const easedProgress = easeOutCubic(progress);
          const currentProgressValue = easedProgress * targetProgress;
          const currentPointsValue = Math.floor(easedProgress * targetPoints);
          const currentPointsToGoValue = Math.ceil(pointsForReward - (easedProgress * targetPoints));

          setAnimatedProgress(currentProgressValue);
          setAnimatedPoints(currentPointsValue);
          setAnimatedPointsToGo(Math.max(targetPointsToGo, currentPointsToGoValue));

          if (progress < 1) {
            requestAnimationFrame(animateProgress);
          } else {
            setAnimatedProgress(targetProgress);
            setAnimatedPoints(targetPoints);
            setAnimatedPointsToGo(targetPointsToGo);
            // Trigger pop animation after a short delay
            setTimeout(() => {
              setShowPopAnimation(true);
              // Remove the animation class after animation completes
              setTimeout(() => {
                setShowPopAnimation(false);
              }, 600); // Match the animation duration
            }, 200);
          }
        };

        requestAnimationFrame(animateProgress);
      }, 300); // Quick delay to see 0% state without being sluggish
    }
  }, [loading, businessData, customerPoints]);

  // Touch-based parallax effect
  const handleTouchMove = (e) => {
    if (!cardRef.current || !isPressed) return;
    
    const rect = cardRef.current.getBoundingClientRect();
    const touch = e.touches[0];
    const x = ((touch.clientX - rect.left) / rect.width - 0.5) * 15;
    const y = ((touch.clientY - rect.top) / rect.height - 0.5) * 15;
    
    setTransform(`perspective(1000px) rotateY(${x}deg) rotateX(${-y}deg) translateZ(20px)`);
    
    // Dynamic shadow based on tilt
    const shadowX = x * 2; // Shadow moves opposite to tilt
    const shadowY = Math.abs(y) * 1.5 + 20; // Shadow gets larger with more tilt
    const shadowBlur = Math.abs(x) + Math.abs(y) + 40; // More blur with more tilt
    const shadowOpacity = 0.4 + (Math.abs(x) + Math.abs(y)) * 0.02; // Darker with more tilt
    
    setShadow(`
      ${-shadowX}px ${shadowY}px ${shadowBlur}px rgba(255, 163, 102, ${shadowOpacity}),
      ${-shadowX * 0.5}px ${shadowY * 0.7}px ${shadowBlur * 0.6}px rgba(0, 0, 0, 0.15),
      ${-shadowX * 0.3}px ${shadowY * 0.4}px ${shadowBlur * 0.3}px rgba(0, 0, 0, 0.1)
    `);
  };

  const handleTouchStart = (e) => {
    setIsPressed(true);
    handleTouchMove(e);
  };

  const handleTouchEnd = () => {
    setIsPressed(false);
    // Return to neutral position with smooth transition
    setTransform('perspective(1000px) rotateY(0deg) rotateX(0deg) translateZ(0px)');
    setShadow('');
  };

  // Mouse events for desktop testing
  const handleMouseDown = (e) => {
    setIsPressed(true);
    handleMouseMove(e);
  };

  const handleMouseMove = (e) => {
    if (!cardRef.current || !isPressed) return;
    
    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 15;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 15;
    
    setTransform(`perspective(1000px) rotateY(${x}deg) rotateX(${-y}deg) translateZ(20px)`);
    
    // Dynamic shadow for mouse as well
    const shadowX = x * 2;
    const shadowY = Math.abs(y) * 1.5 + 20;
    const shadowBlur = Math.abs(x) + Math.abs(y) + 40;
    const shadowOpacity = 0.4 + (Math.abs(x) + Math.abs(y)) * 0.02;
    
    setShadow(`
      ${-shadowX}px ${shadowY}px ${shadowBlur}px rgba(255, 163, 102, ${shadowOpacity}),
      ${-shadowX * 0.5}px ${shadowY * 0.7}px ${shadowBlur * 0.6}px rgba(0, 0, 0, 0.15),
      ${-shadowX * 0.3}px ${shadowY * 0.4}px ${shadowBlur * 0.3}px rgba(0, 0, 0, 0.1)
    `);
  };

  const handleMouseUp = () => {
    setIsPressed(false);
    setTransform('perspective(1000px) rotateY(0deg) rotateX(0deg) translateZ(0px)');
    setShadow('');
  };

  const handleMouseLeave = () => {
    setIsPressed(false);
    setTransform('perspective(1000px) rotateY(0deg) rotateX(0deg) translateZ(0px)');
    setShadow('');
  };

  if (loading) {
    return (
      <div className="loading">
        <p style={{ 
          fontFamily: 'Poppins, sans-serif', 
          color: '#000000',
          fontSize: '18px',
          fontWeight: '500',
          marginBottom: '20px'
        }}>
          Loading Rewards...
        </p>
        <img 
          src={logo} 
          alt="Bounce Loyalty" 
          style={{ 
            width: '120px', 
            height: 'auto',
            display: 'block'
          }} 
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="error">
        <h2>Error Loading Reward</h2>
        <p>{error}</p>
        <p style={{fontSize: '12px', marginTop: '10px', opacity: 0.7}}>Check browser console for detailed logs</p>
        <button onClick={() => navigate('/')}>Go Home</button>
        <button onClick={() => window.location.reload()} style={{marginLeft: '10px'}}>Retry</button>
      </div>
    );
  }

  if (!businessData) {
    return (
      <div className="error">
        <h2>Data Not Available</h2>
        <p>Business data not available</p>
        <p style={{fontSize: '12px', marginTop: '10px', opacity: 0.7}}>Check browser console for detailed logs</p>
        <button onClick={() => navigate('/')}>Go Home</button>
        <button onClick={() => window.location.reload()} style={{marginLeft: '10px'}}>Retry</button>
      </div>
    );
  }

  // Extract business data
  const pointsForReward = businessData.settings?.pointsforreward || 1200;
  const pointsEarned = customerPoints || 0;
  const businessName = businessData.businessname || 'Business';
  const rewardType = businessData.settings?.rewarddescription || 'a free reward';
  const color = businessData.settings?.brandcolour || '#FFA366';
  const brandLogo = businessData.settings?.brandlogo;
  // Use 0 until animation starts to prevent any jumping
  const progressPercentage = showAnimation ? animatedProgress : 0;
  
  // Determine text color based on background color
  const textColor = isLightColor(color || '#FFA366') ? '#666666' : 'white';
  
  // Horseshoe parameters
  const centerX = 150;
  const centerY = 150;
  const radius = 135;
  const gapAngle = 70; // degrees for the bottom gap (35 degrees on each side)
  const startAngle = 90 + (gapAngle / 2); // Start angle in degrees
  const endAngle = 450 - (gapAngle / 2); // End angle in degrees (360 + 90 - gap/2)
  const totalAngle = endAngle - startAngle; // Total arc angle
  
  // Convert angles to radians and calculate arc path
  const startAngleRad = (startAngle * Math.PI) / 180;
  const endAngleRad = (endAngle * Math.PI) / 180;
  
  const startX = centerX + radius * Math.cos(startAngleRad);
  const startY = centerY + radius * Math.sin(startAngleRad);
  const endX = centerX + radius * Math.cos(endAngleRad);
  const endY = centerY + radius * Math.sin(endAngleRad);
  
  // Create the horseshoe path
  const horseshoePath = `M ${startX} ${startY} A ${radius} ${radius} 0 1 1 ${endX} ${endY}`;
  
  // Calculate progress arc
  const progressAngle = (progressPercentage / 100) * totalAngle;
  const progressEndAngleRad = (startAngle + progressAngle) * Math.PI / 180;
  const progressEndX = centerX + radius * Math.cos(progressEndAngleRad);
  const progressEndY = centerY + radius * Math.sin(progressEndAngleRad);
  
  // Determine if we need the large arc flag for progress
  const largeArcFlag = progressAngle > 180 ? 1 : 0;
  
  const progressPath = progressPercentage > 0 
    ? `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${progressEndX} ${progressEndY}`
    : '';

  // Handler to claim tap (call cloud function)
  const handleClaimTap = async (sid) => {
    try {
      console.log('[RewardEarned] Claiming tap with session:', sid);
      const result = await tapSessionService.claimTap(sid);
      console.log('[RewardEarned] Tap claimed successfully:', result);
      
      // Update customer points with the new total from cloud function result
      if (result.totalPoints !== undefined) {
        setCustomerPoints(result.totalPoints);
      }
      
      // Refresh customer data from Firestore to get the latest points
      if (sessionData?.businessUid && user) {
        const updatedCustomer = await customerService.getCustomerData(user.uid);
        if (updatedCustomer?.businessAssociations?.[sessionData.businessUid]) {
          const latestPoints = updatedCustomer.businessAssociations[sessionData.businessUid].pointsBalance || 0;
          console.log('[RewardEarned] Updated points from Firestore:', latestPoints);
          setCustomerPoints(latestPoints);
        }
      }
      
      // Refresh customer data in context
      await refreshCustomerData();
      
      return result;
    } catch (error) {
      console.error('[RewardEarned] Error claiming tap:', error);
      setAuthError('Failed to claim tap. Please try again.');
      throw error;
    }
  };

  const handleClaim = async () => {
    // If user is not authenticated, show signup form
    if (!user) {
      setCardBackView('signup');
      setIsFlipped(true);
      return;
    }
    
    // If user is authenticated, claim the tap
    const sessionId = searchParams.get('sid');
    if (sessionId && user && sessionData) {
      try {
        // Ensure customer profile and business association exist before claiming
        console.log('[RewardEarned] Ensuring customer profile exists for:', user.uid);
        await authService.createCustomerProfile(
          user.uid, 
          sessionData.businessUid, 
          user.email
        );
        console.log('[RewardEarned] Customer profile ensured, claiming tap');
        
        await handleClaimTap(sessionId);
        // After claiming, check if they have enough points to redeem
        const pointsForReward = businessData.settings?.pointsforreward || 1200;
        if (customerPoints >= pointsForReward) {
          // TODO: Navigate to redemption flow
          alert('Congratulations! You can now redeem your reward!');
        }
      } catch (error) {
        console.error('[RewardEarned] Error claiming tap:', error);
        setAuthError('Failed to claim tap. Please try again.');
      }
    }
  };

  const handleHowItWorks = () => {
    setCardBackView('howItWorks');
    setIsFlipped(!isFlipped);
  };

  const handleBounceLogoClick = () => {
    navigate('/');
  };

  const handleEmailSignup = async () => {
    if (!email || !email.includes('@')) {
      setAuthError('Please enter a valid email address');
      return;
    }

    setAuthLoading(true);
    setAuthError(null);

    try {
      const currentUrl = window.location.href;
      await authService.sendPasswordlessEmail(email, currentUrl);
      
      // Save email to Emails Submitted collection
      const businessUid = sessionData?.businessUid || null;
      await emailCollectionService.saveSubmittedEmail(email, businessUid);
      
      setEmailSent(true);
      console.log('[RewardEarned] Password less email sent to:', email);
    } catch (error) {
      console.error('[RewardEarned] Error sending email:', error);
      setAuthError(error.message || 'Failed to send email. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogin = async () => {
    // For passwordless, login is the same as signup
    await handleEmailSignup();
  };

  return (
    <div className="reward-earned-page">
      <div 
        ref={cardRef}
        className={`reward-container ${isFlipped ? 'flipped' : ''}`}
        style={{ 
          transform: transform,
          boxShadow: shadow
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <div className="card-inner">
          <div 
            className="card-front"
            style={{ backgroundColor: color || '#FFA366' }}
          >
        <div className="header">
          {brandLogo ? (
            <img src={brandLogo} alt={businessName} className="coffee-logo" />
          ) : (
            <div className="business-name-header" style={{ color: textColor }}>
              {businessName}
            </div>
          )}
        </div>

        <div className="horseshoe-container">
          <div className="horseshoe-progress">
            <svg className="horseshoe-svg" viewBox="0 0 300 300">
              {/* Background horseshoe */}
              <path
                d={horseshoePath}
                fill="none"
                stroke={isLightColor(color || '#FFA366') ? "rgba(102, 102, 102, 0.3)" : "rgba(255, 255, 255, 0.3)"}
                strokeWidth="20"
                strokeLinecap="round"
              />
              {/* Progress horseshoe */}
              {progressPath && (
                <path
                  d={progressPath}
                  fill="none"
                  stroke="url(#gradient)"
                  strokeWidth="20"
                  strokeLinecap="round"
                  className="progress-arc"
                />
              )}
              {/* Gradient definition */}
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor={isLightColor(color || '#FFA366') ? "#666666" : "white"} />
                  <stop offset="100%" stopColor={isLightColor(color || '#FFA366') ? "rgba(102, 102, 102, 0.8)" : "rgba(255, 255, 255, 0.8)"} />
                </linearGradient>
              </defs>
            </svg>
            
            <div className="points-display">
              <div className={`points-text ${showPopAnimation ? 'pop-animation' : ''}`} style={{ color: textColor }}>
                <span className="earned-points">{showAnimation ? animatedPoints : 0}</span>
                <span className="separator"> of </span>
                <span className="total-points">{pointsForReward}</span>
              </div>
              <div className="points-label" style={{ color: textColor, opacity: 0.8 }}>points earned</div>
            </div>
          </div>
        </div>

        <div className="reward-description">
          <p style={{ color: textColor }}>
            You're earning towards <strong style={{ color: textColor, opacity: 0.9 }}>{rewardType}</strong> at <strong style={{ color: textColor, opacity: 0.9 }}>{businessName}</strong>
          </p>
        </div>

        <div className="action-buttons">
          <button 
            className="claim-button"
            style={{ color: color ? darkenColor(color, 0.4) : '#B8732E' }}
            onClick={handleClaim}
            disabled={authLoading || user}
          >
{!user 
  ? "Start Collecting" 
  : (pointsEarned >= pointsForReward ? 'CLAIM REWARD' : `${showAnimation ? animatedPointsToGo : pointsForReward} points to go`)}
          </button>
        </div>

        <div className="how-it-works-text" onClick={handleHowItWorks}>
          How does this work?
        </div>
          </div>
          
          <div 
            className="card-back"
            style={{ backgroundColor: color || '#FFA366' }}
            onClick={cardBackView === 'howItWorks' ? handleHowItWorks : undefined}
          >
            <div className="card-back-content">
              {cardBackView === 'howItWorks' ? (
                <>
                  <h3 style={{ color: textColor }}>How it works</h3>
                  <p style={{ color: textColor }}>
                    Keep tapping your phone on the pod every time you make a purchase. When you get enough points, hit claim and show your reward to the cashier!
                  </p>
                </>
              ) : (
                <>
                  <h3 style={{ color: textColor }}>
                    {emailSent ? 'Check your email!' : 'Join to start collecting points'}
                  </h3>
                  
                  {emailSent ? (
                    <div style={{ padding: '20px', textAlign: 'center' }}>
                      <p style={{ color: textColor, marginBottom: '15px' }}>
                        We sent a magic link to <strong>{email}</strong>
                      </p>
                      <p style={{ color: textColor, fontSize: '14px', opacity: 0.8 }}>
                        Click the link in your email to complete sign-in and claim your points!
                      </p>
                      <button
                        onClick={() => {
                          setEmailSent(false);
                          setEmail('');
                        }}
                        style={{
                          marginTop: '20px',
                          padding: '10px 20px',
                          backgroundColor: 'transparent',
                          color: textColor,
                          border: `1px solid ${textColor}`,
                          borderRadius: '5px',
                          cursor: 'pointer'
                        }}
                      >
                        Try a different email
                      </button>
                    </div>
                  ) : (
                    <div className="signup-form">
                      {authError && (
                        <p style={{ color: 'red', fontSize: '14px', marginBottom: '10px' }}>
                          {authError}
                        </p>
                      )}
                      
                      <input
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleEmailSignup();
                          }
                        }}
                        disabled={authLoading}
                        style={{
                          width: '100%',
                          padding: '12px',
                          marginBottom: '15px',
                          border: `2px solid ${color || '#FFA366'}`,
                          borderRadius: '8px',
                          fontSize: '16px',
                          boxSizing: 'border-box'
                        }}
                      />
                      
                      <div className="signup-buttons">
                        <button 
                          className="signup-button email-signup"
                          onClick={handleEmailSignup}
                          disabled={authLoading}
                          style={{ 
                            backgroundColor: color || '#FFA366',
                            color: 'white',
                            border: `2px solid ${color || '#FFA366'}`,
                            opacity: authLoading ? 0.6 : 1,
                            cursor: authLoading ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {authLoading ? 'Sending...' : 'Send Magic Link'}
                        </button>
                      </div>
                      
                      <p style={{ 
                        color: textColor, 
                        fontSize: '12px', 
                        marginTop: '15px',
                        opacity: 0.7,
                        textAlign: 'center'
                      }}>
                        No password needed! We'll send you a link to sign in.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bounce-logo-container">
        <img 
          src={logo} 
          alt="Powered by BounceLoyalty" 
          className="bounce-logo" 
          onClick={handleBounceLogoClick}
        />
      </div>

      <div className="legal-links">
        <a href="/privacy-policy" className="legal-link">Privacy Policy</a>
        <span className="legal-separator">•</span>
        <a href="/terms-of-service" className="legal-link">Terms of Service</a>
      </div>
    </div>
  );
};

export default RewardEarnedPage;